import { pricingFallbackViolation } from '@listening/api-contract/pricing-experiment';
import type { FeatureFlagDef } from './flag-types';
import { EdgeClientFlags } from './edge-client-flags';

// Edge-bucketed flags live in their own module so the CloudFront edge bundle
// can import them in isolation — see `./edge-client-flags`. Re-exported here so
// `@listening/frontend/analytics/flags/client` consumers can still reach them.
export { EdgeClientFlags } from './edge-client-flags';

// Non-edge flags: PostHog `user`-bucketed experiments plus `local` dev
// toggles. NEVER import this (or the combined `ClientFlags`) into the edge
// bundle — only `EdgeClientFlags`. These resolve client-side / SSR only.
// `pricingTestV1` used to live here as a `user`-mode (PostHog-bucketed) flag.
// It moved to `EdgeClientFlags` when the experiment adopted Mode C: the edge
// cookie assigns the arm so anonymous marketing surfaces can render it from a
// shared CDN cache, and signup pins it to the account.
//
// It could not stay in both registries. `ClientFlags` below spreads
// `NonEdgeClientFlags` last, so a same-named entry here would SHADOW the edge
// def for every app-side reader — `useFlag` would report `mode: 'user'`, the
// `x-variants` assignment would be ignored, and Mode C would be silently off
// with nothing failing. One name, one registry.
const NonEdgeClientFlags = {
  /**
   * The three public-doc presentation tests — PAUSED, and `local` says so.
   *
   * They bucketed at the edge on `/doc/$docId` until `pricing-test-v1` adopted
   * Mode C. Six arms plus the country bit spend nearly the whole CloudFront
   * viewer-request budget (3,643 of 3,674 bytes), and re-registering even ONE
   * of these measured 4,446 — there is no arrangement where both programs
   * bucket at the edge today. `/doc/$docId` also cost 27 CloudFront cache
   * entries against a 12 cap. Running both is a live TODO, not a decision to
   * drop these: docs/projects/backlog/web/edge-budget-two-experiment-programs.md
   * has the measurements, the two budgets, and the ways out.
   *
   * `local`, not `user`: `/doc/$docId` is `cdn-shared`, and PostHog-bucketed
   * `user` mode needs per-user HTML. Declaring `user` would read as a running
   * experiment while the shared CDN cache served one arm to everyone. `local`
   * is honest — every visitor gets `control`, nothing is reported, and the
   * DevTools panel can still flip them for QA.
   *
   * Their PostHog flags and experiments stay declared in
   * `packages/infra/src/posthog/definitions/` so
   * the next push does not hard-delete them.
   */
  marketingPublicDocGiantTitleV1: {
    key: 'marketing-public-doc-giant-title-v1',
    label: 'Giant Title On Top (Centered)',
    variants: ['control', 'treatment'] as const,
    variantLabels: { control: 'Hide title', treatment: 'Show title' },
    default: 'control' as const,
    bucketing: { mode: 'local' },
  },
  marketingPublicDocReadTimeV1: {
    key: 'marketing-public-doc-reading-time-v1',
    label: 'How Long It Is to Read',
    variants: ['control', 'treatment'] as const,
    variantLabels: {
      control: 'Hide reading time',
      treatment: 'Show reading time',
    },
    default: 'control' as const,
    bucketing: { mode: 'local' },
  },
  marketingPublicDocOverviewPopupV1: {
    key: 'marketing-public-doc-overview-popup-v1',
    label: 'Overview / Main Ideas Popup',
    variants: ['control', 'treatment'] as const,
    variantLabels: {
      control: 'Keep current sidebar',
      treatment: 'Use overview popup',
    },
    default: 'control' as const,
    bucketing: { mode: 'local' },
  },
  // Local dev toggles (override via console: analytics.flags.set('simulateFreeCountry', 'true')).
  // NOTE: these move only the CLIENT's belief. Since the free-plan upload quota
  // is now enforced server-side (FREE_UPLOAD_QUOTA_ENFORCEMENT), toggling them
  // does NOT bypass a real 429/403 once enforcement is 'on' — the server decides
  // from the trusted CloudFront geo header, not the client. To exercise the
  // enforced path locally, set the server config, don't flip these.
  simulateFreeCountry: {
    key: 'simulate-free-country',
    variants: ['true', 'false'] as const,
    default: 'false' as const,
    bucketing: { mode: 'local' },
  },
  // UI-only: suppresses the client-side pre-block so the upload UI proceeds.
  // Does not affect server enforcement (renamed from bypassFreePlanUploadLimit
  // to stop implying it bypasses the quota end-to-end).
  bypassClientUploadPrecheck: {
    key: 'bypass-client-upload-precheck',
    variants: ['true', 'false'] as const,
    default: 'false' as const,
    bucketing: { mode: 'local' },
  },
  bypassFreePlanPrivateAudioLimit: {
    key: 'bypass-free-plan-private-audio-limit',
    variants: ['true', 'false'] as const,
    default: 'false' as const,
    bucketing: { mode: 'local' },
  },
  fastDarkModeSwitch: {
    key: 'fast-dark-mode-switch',
    variants: ['true', 'false'] as const,
    default: 'false' as const,
    bucketing: { mode: 'local' },
  },
  mockSubscriptionEndpoint: {
    key: 'mock-subscription-endpoint',
    variants: ['true', 'false'] as const,
    default: 'false' as const,
    bucketing: { mode: 'local' },
  },
  disableDocPackLoading: {
    key: 'disable-doc-pack-loading',
    variants: ['true', 'false'] as const,
    default: 'false' as const,
    bucketing: { mode: 'local' },
  },
  usePageImagePdfRendering: {
    key: 'use-page-image-pdf-rendering',
    variants: ['true', 'false'] as const,
    default: 'false' as const,
    bucketing: { mode: 'local' },
  },
} as const satisfies Record<string, FeatureFlagDef>;

// Combined registry for app-side consumers (`useFlag`, console API, type
// derivations like `EdgeBucketedFlagKey`). The edge bundle must import
// `EdgeClientFlags` from `./edge-client-flags` instead — see that file.
export const ClientFlags = {
  ...EdgeClientFlags,
  ...NonEdgeClientFlags,
} satisfies Record<string, FeatureFlagDef>;

export type ClientFlagName = keyof typeof ClientFlags;

// C4 refused at load time, not caught by a test someone has to run: a price
// experiment whose fallback is not its top-priced arm would advertise less
// to crawlers than checkout can charge. Throwing here follows the repo's
// panic-on-broken-config rule — the registry cannot load with a bad pricing
// definition. It lives HERE and not in `edge-client-flags.ts` because that
// module is value-reachable from the CloudFront bundle, where a module-scope
// side effect cannot be tree-shaken; this one never is. Any future price
// experiment must add its own assert alongside this one.
// The presence guard exists for test files that `vi.mock` the registry with
// fixtures omitting the pricing flag; the REAL definition can never skip it
// (pricing-split-parity.test.ts asserts the same property on the real def).
{
  const pricingDef: FeatureFlagDef | undefined = ClientFlags.pricingTestV1;
  if (pricingDef !== undefined && pricingDef.bucketing.mode === 'edge') {
    const violation = pricingFallbackViolation({
      key: pricingDef.key,
      variants: pricingDef.variants,
      split: pricingDef.bucketing.split,
      fallbackVariant: pricingDef.bucketing.fallbackVariant,
    });
    if (violation !== null) throw new Error(violation);
  }
}
